import { createFileRoute } from "@tanstack/react-router";
import { images } from "@/data/images";
import { Section, SectionHeading } from "@/components/site/Section";
import { ReviewsBlock } from "@/components/site/ReviewsBlock";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { ReviewBar } from "@/components/site/ReviewBar";
import { localBusinessSchema, meta } from "@/lib/schema";
import { overviewKeywords } from "@/lib/localKeywords";

const title = "Over Vakkundig Verhuizingen & Transport | Zuid-Holland";
const description =
  "Vakkundig Verhuizingen & Transport B.V.: sinds 2020 een erkend verhuisbedrijf uit Zuid-Holland met NIWO-vergunning, eigen wagenpark en verhuislift. Particulier en zakelijk.";

const story = [
  "Vakkundig Verhuizingen & Transport is in 2020 ontstaan vanuit een bijzondere periode. Tijdens de coronapandemie kwam de evenementenbranche vrijwel volledig stil te liggen. Omdat evenementenwerk tijdelijk wegviel, besloten we niet stil te zitten. Transport ging immers altijd door. Vanuit die gedachte begonnen we met één bus en de overtuiging om klanten betrouwbaar, zorgvuldig en vakkundig te helpen.",
  "Wat begon als een klein initiatief, groeide in de afgelopen jaren uit tot een professioneel bedrijf en inmiddels een B.V. Die groei hebben we niet alleen aan onszelf te danken. Familie, vrienden, collega’s en onze relaties hebben vanaf het begin geholpen, meegedacht en vertrouwen in ons gehad. Daar zijn we ontzettend dankbaar voor.",
  "Inmiddels hebben we ruim zes jaar ervaring en voeren we iedere dag uiteenlopende opdrachten uit. Van een kleine particuliere verhuizing tot zakelijke verhuizingen, transport en grote logistieke opdrachten. Ook bij bijzondere projecten staan we klaar: zo hebben we meegewerkt aan de opbouw en logistiek rondom de NAVO-top.",
  "Voor ons is iedere opdracht belangrijk. Of het nu om een paar meubels gaat of om een groot evenement met strakke planning: we werken met dezelfde aandacht, zorg en verantwoordelijkheid. We vinden het belangrijk dat klanten weten wie er voor hun deur staat en dat hun spullen met respect worden behandeld.",
  "Onze naam zegt precies waar wij voor staan: Vakkundig. We doen ons werk iedere dag met veel plezier, betrokkenheid en passie. Dankzij het vertrouwen van onze klanten, relaties en alle betrokken collega’s kunnen we blijven groeien en ons iedere dag verder ontwikkelen.",
  "Aan iedereen die vanaf het begin in ons heeft geloofd en aan iedereen met wie we vandaag samenwerken: bedankt voor deze mooie reis.",
];

export const Route = createFileRoute("/over-ons")({
  head: () => meta({
      title,
      description,
      path: "/over-ons",
      image: "team",
      keywords: overviewKeywords(["betrouwbaar verhuisbedrijf", "erkend verhuisbedrijf den haag", "verhuisbedrijf met eigen personeel"]),
    }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <JsonLd data={localBusinessSchema} />

      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Over Vakkundig Verhuizingen & Transport"
          title="Van één bus naar een professioneel verhuisbedrijf"
          inverted
        />
      </Section>

      <ReviewBar />

      <Section>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4">
            {story.map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className="leading-relaxed text-muted-foreground">
                {paragraph}
              </p>
            ))}
            <p className="pt-2 font-display text-lg font-bold text-secondary">
              Vakkundig Verhuizingen &amp; Transport — persoonlijk betrokken, professioneel
              uitgevoerd.
            </p>
          </div>
          <img
            src={images.team}
            alt="Het wagenpark van Vakkundig Verhuizingen & Transport op de standplaats"
            className="rounded-2xl object-cover shadow-[var(--shadow-card)]"
            loading="lazy"
          />
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading eyebrow="Ervaringen" title="Beoordelingen van onze klanten" />
        <div className="mt-8">
          <ReviewsBlock limit={6} />
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
